import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, AlertTriangle, Clock, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import ActionStatusBadge from "@/components/ActionStatusBadge";
import { cn } from "@/lib/utils";

/**
 * Actions correctives rattachées à un litige.
 *
 * La relation est portée par l'ACTION (`complaint_id`) ; le litige n'en garde
 * aucune copie. Ce bloc interroge donc l'API à chaque affichage plutôt que de
 * lire une liste stockée sur la fiche : une liste dupliquée divergerait dès la
 * première modification de l'action, sans que rien ne le signale — c'est le
 * même principe que `parent_complaint_id`, porté par l'enfant seul.
 */
const LIBELLES_VERIFICATION = {
  a_verifier: "À vérifier",
  efficace: "Efficace",
  inefficace: "Inefficace",
  a_reouvrir: "À rouvrir",
};

const STYLES_VERIFICATION = {
  a_verifier: "bg-amber-50 text-amber-800",
  efficace: "bg-emerald-50 text-emerald-800",
  inefficace: "bg-rose-50 text-rose-700",
  a_reouvrir: "bg-blue-50 text-blue-800",
};

function estEnRetard(action) {
  if (!action.due_date || action.status === "cloturee") return false;
  return action.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
}

export default function ComplaintCorrectiveActions({ complaintId, peutCreer = false }) {
  const [actions, setActions] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [accessible, setAccessible] = useState(true);
  const navigate = useNavigate();

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/corrective-actions", {
        params: { complaint_id: complaintId },
      });
      setActions(data || []);
    } catch {
      // Le module est fermé au commercial : le bloc s'efface au lieu
      // d'afficher une erreur pour une absence de droit parfaitement normale.
      setAccessible(false);
    } finally {
      setChargement(false);
    }
  }, [complaintId]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!accessible) return null;

  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
      data-testid="complaint-corrective-actions"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-slate-400">
          <ClipboardCheck size={15} strokeWidth={1.75} />
          Actions correctives
        </h2>
        {peutCreer ? (
          <button
            type="button"
            onClick={() => navigate(`/actions?complaint_id=${complaintId}`)}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50"
            data-testid="create-corrective-action"
          >
            <Plus size={13} /> Créer une action corrective
          </button>
        ) : null}
      </div>

      {chargement ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
        </div>
      ) : actions.length === 0 ? (
        <p className="py-4 text-sm text-slate-400" data-testid="no-corrective-action">
          Aucune action corrective associée.
        </p>
      ) : (
        <ul className="divide-y divide-slate-50">
          {actions.map((action) => {
            const retard = estEnRetard(action);
            return (
              <li key={action.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/actions?focus=${action.id}`)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 py-3 text-left transition-colors hover:bg-slate-50"
                  data-testid={`corrective-action-${action.id}`}
                >
                  <span className="font-heading text-sm font-bold text-slate-900">
                    {action.number}
                  </span>
                  <ActionStatusBadge status={action.status} />
                  {action.verification_status ? (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                        STYLES_VERIFICATION[action.verification_status] || "bg-slate-100 text-slate-600",
                      )}
                      title="Vérification d'efficacité"
                    >
                      {LIBELLES_VERIFICATION[action.verification_status] || action.verification_status}
                    </span>
                  ) : null}

                  <span className="w-full truncate text-sm text-slate-700 sm:w-auto sm:flex-1">
                    {action.action}
                  </span>

                  <span className="text-xs text-slate-500">
                    {action.responsible_name || "Sans responsable"}
                  </span>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs font-medium",
                      retard ? "text-rose-600" : "text-slate-500",
                    )}
                    data-testid={retard ? `corrective-action-overdue-${action.id}` : undefined}
                  >
                    {retard ? <AlertTriangle size={12} /> : <Clock size={12} strokeWidth={1.5} />}
                    {(action.due_date || "").slice(0, 10)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
